import { useEffect, useRef, useState, type RefObject } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

// Register useGSAP plugin once
gsap.registerPlugin(useGSAP);

export { gsap, useGSAP };

/**
 * Check if the user has requested reduced motion.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export interface CountUpOptions {
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  ease?: string;
}

/**
 * GSAP Count-up hook for KPI numbers and metrics.
 * Smoothly interpolates from previous value to target value.
 */
export function useCountUp(
  targetValue: number,
  options: CountUpOptions = {}
): string {
  const {
    duration = 0.75,
    decimals = 0,
    prefix = "",
    suffix = "",
    ease = "power2.out",
  } = options;

  const [displayValue, setDisplayValue] = useState<string>(() => {
    if (isNaN(targetValue)) return `${prefix}0${suffix}`;
    return `${prefix}${targetValue.toFixed(decimals)}${suffix}`;
  });

  const valueRef = useRef({ val: targetValue });
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isNaN(targetValue)) return;

    if (isFirstRender.current || prefersReducedMotion()) {
      isFirstRender.current = false;
      valueRef.current.val = targetValue;
      setDisplayValue(`${prefix}${targetValue.toFixed(decimals)}${suffix}`);
      return;
    }

    const tween = gsap.to(valueRef.current, {
      val: targetValue,
      duration,
      ease,
      onUpdate: () => {
        setDisplayValue(`${prefix}${valueRef.current.val.toFixed(decimals)}${suffix}`);
      },
    });

    return () => {
      tween.kill();
    };
  }, [targetValue, duration, decimals, prefix, suffix, ease]);

  return displayValue;
}

/**
 * GSAP hook to stagger-animate child elements inside a container.
 */
export function useStaggerEntrance(
  containerRef: RefObject<HTMLElement | null>,
  selector: string = ".stagger-item",
  deps: unknown[] = []
) {
  useGSAP(
    () => {
      if (!containerRef.current || prefersReducedMotion()) return;
      const elements = containerRef.current.querySelectorAll(selector);
      if (!elements.length) return;

      gsap.from(elements, {
        opacity: 0,
        y: 12,
        duration: 0.35,
        stagger: 0.04,
        ease: "power2.out",
        clearProps: "all",
      });
    },
    { scope: containerRef, dependencies: deps, revertOnUpdate: true }
  );
}

/**
 * GSAP pulse animation utility for live status badges or active indicators.
 */
export function pulseElement(element: HTMLElement) {
  if (prefersReducedMotion() || !element) return;
  gsap.fromTo(
    element,
    { scale: 0.96, opacity: 0.7 },
    { scale: 1, opacity: 1, duration: 0.3, ease: "back.out(1.7)" }
  );
}

/**
 * GSAP theme toggle animation for tactile icon rotation and button spring.
 */
export function animateThemeToggle(
  button: HTMLElement | null,
  icon: HTMLElement | null,
  isDark: boolean
) {
  if (prefersReducedMotion()) return;
  if (button) {
    gsap.fromTo(
      button,
      { scale: 0.86 },
      { scale: 1, duration: 0.32, ease: "back.out(2.2)" }
    );
  }
  if (icon) {
    gsap.fromTo(
      icon,
      { rotation: isDark ? -120 : 120, scale: 0.35, opacity: 0 },
      { rotation: 0, scale: 1, opacity: 1, duration: 0.38, ease: "back.out(2.4)" }
    );
  }
}

